import React, { useState, useEffect } from 'react';
import { Calendar, ChevronDown, ChevronUp, Search, UserCheck, UserX, ChevronLeft, ChevronRight, Eye } from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import "../../css/AttendanceView.css";
import Loader from '../../components/Loader';

function AttendanceView() {
    const [activeUserType, setActiveUserType] = useState('student');
    const [users, setUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
    const [monthlyAttendance, setMonthlyAttendance] = useState({});
    const [expandedUser, setExpandedUser] = useState(null);
    const [yearlyAttendance, setYearlyAttendance] = useState({});

    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    useEffect(() => {
        fetchUsers();
    }, [activeUserType]);

    useEffect(() => {
        if (users.length > 0) {
            fetchMonthlyAttendance();
        }
    }, [users, currentMonth, currentYear, activeUserType]);

    const fetchUsers = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            toast.error("Unauthorized! Please login again.");
            return;
        }

        setIsLoading(true);
        try {
            const endpoint = activeUserType === 'student' ? '/api/students' : '/api/teachers';
            const response = await fetch(`https://schoolpro-api.uniums.com${endpoint}`, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to fetch users');
            }

            const data = await response.json();

            if (Array.isArray(data)) {
                setUsers(data);
                if (data.length === 0) {
                    toast.info(`No ${activeUserType}s found`);
                }
            } else {
                throw new Error('Invalid data format received');
            }
        } catch (error) {
            console.error('Fetch Error:', error);
            toast.error(error.message);
            setUsers([]);
        } finally {
            setIsLoading(false);
        }
    };
    const fetchMonthlyAttendance = async () => {
        const token = localStorage.getItem("token");
        if (!token) return;

        setIsLoading(true);
        try {
            const response = await fetch(
                `https://schoolpro-api.uniums.com/api/attendance/monthly/${activeUserType}?month=${currentMonth + 1
                }&year=${currentYear}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch attendance data");
            }

            const data = await response.json();
            console.log("Monthly Attendance Data:", data); // Add this to debug

            // Ensure data is in the expected format
            if (!data.success || !Array.isArray(data.data)) {
                throw new Error("Invalid attendance data format");
            }

            // Group attendance by userId
            const attendanceByUser = {};
            data.data.forEach((item) => {
                if (!attendanceByUser[item.userId]) {
                    attendanceByUser[item.userId] = [];
                }
                attendanceByUser[item.userId].push(item);
            });

            setMonthlyAttendance(attendanceByUser);
        } catch (error) {
            console.error("Fetch Attendance Error:", error);
            toast.error("Failed to load attendance data: " + error.message);
            setMonthlyAttendance({});
        } finally {
            setIsLoading(false);
        }
    };

    const fetchYearlyAttendance = async (userId) => {
        const token = localStorage.getItem("token");
        if (!token) return;
    
        try {
            const response = await fetch(
                `https://schoolpro-api.uniums.com/api/attendance/yearly/${activeUserType}/${userId}?year=${currentYear}`,
                {
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );
    
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch yearly attendance data");
            }
    
            const data = await response.json();
    
            // Validate response structure
            if (!data.success || !Array.isArray(data.data)) {
                throw new Error("Invalid yearly attendance data format");
            }
    
            setYearlyAttendance((prev) => ({
                ...prev,
                [userId]: data.data, // Store only the array of attendance records
            }));
        } catch (error) {
            console.error("Fetch Yearly Attendance Error:", error);
            toast.error("Failed to load yearly attendance data: " + error.message);
        }
    };
    const handleMonthChange = (increment) => {
        let newMonth = currentMonth + increment;
        let newYear = currentYear;

        if (newMonth > 11) {
            newMonth = 0;
            newYear += 1;
        } else if (newMonth < 0) {
            newMonth = 11;
            newYear -= 1;
        }

        setCurrentMonth(newMonth);
        setCurrentYear(newYear);
    };

    const toggleUserDetails = async (userId) => {
        if (expandedUser === userId) {
            setExpandedUser(null);
        } else {
            setExpandedUser(userId);
            if (!yearlyAttendance[userId]) {
                await fetchYearlyAttendance(userId);
            }
        }
    };

    const getDaysInMonth = (month, year) => {
        return new Date(year, month + 1, 0).getDate();
    };

    const getAttendanceStatus = (userId, day) => {
        const userAttendance = monthlyAttendance[userId] || [];
        const dayAttendance = userAttendance.find((record) =>
            new Date(record.date).getDate() === day
        );
        return dayAttendance ? dayAttendance.status : null;
    };


    const getMonthlyStats = (userId) => {
        const userAttendance = monthlyAttendance[userId] || [];
        const totalDays = userAttendance.length;
        const presentDays = userAttendance.filter(record => record.status === 'present').length;
        const absentDays = totalDays - presentDays;
        const attendancePercentage = totalDays > 0 ? ((presentDays / totalDays) * 100).toFixed(1) : 0;

        return { totalDays, presentDays, absentDays, attendancePercentage };
    };

    const getYearlyMonthStats = (userId, monthIndex) => {
        const userYearlyData = yearlyAttendance[userId] || [];
        const monthData = userYearlyData.filter(record =>
            new Date(record.date).getMonth() === monthIndex
        );

        const totalDays = monthData.length;
        const presentDays = monthData.filter(record => record.status === 'present').length;

        return { totalDays, presentDays };
    };

    const renderCalendarDays = (userId) => {
        const days = getDaysInMonth(currentMonth, currentYear);
        const dayItems = [];

        for (let day = 1; day <= days; day++) {
            const status = getAttendanceStatus(userId, day);
            let statusClass = "";
            let statusIcon = null;

            if (status === "present") {
                statusClass = "present";
                statusIcon = <UserCheck size={14} />;
            } else if (status === "absent") {
                statusClass = "absent";
                statusIcon = <UserX size={14} />;
            }

            dayItems.push(
                <div
                    key={`day-${day}`}
                    className={`attendanceView-calendar-day ${statusClass}`}
                    title={
                        status
                            ? `${day}: ${status.charAt(0).toUpperCase() + status.slice(1)}`
                            : `${day}: No record`
                    }
                >
                    <span className="attendanceView-day-number">{day}</span>
                    {statusIcon && (
                        <span className="attendanceView-day-status">{statusIcon}</span>
                    )}
                </div>
            );
        }

        return dayItems;
    };

    const renderYearlyAttendance = (userId) => {
        if (!yearlyAttendance[userId]) return
        <>
            <Loader />
            <div className="attendanceView-loading">Loading yearly data...</div>
        </>;

        return (
            <div className="attendanceView-yearly-container">
                <h4 className="attendanceView-yearly-title">
                    {currentYear} Attendance Summary
                </h4>
                <div className="attendanceView-yearly-grid">
                    {months.map((month, index) => {
                        const { totalDays, presentDays } = getYearlyMonthStats(userId, index);
                        const percentage = totalDays > 0 ? ((presentDays / totalDays) * 100).toFixed(1) : 0;

                        return (
                            <div key={month} className="attendanceView-yearly-month">
                                <h5 className="attendanceView-month-name">{month}</h5>
                                <div className="attendanceView-month-stats">
                                    <div className="attendanceView-stat">
                                        <span className="attendanceView-stat-value">{totalDays}</span>
                                        <span className="attendanceView-stat-label">Days</span>
                                    </div>
                                    <div className="attendanceView-stat present">
                                        <span className="attendanceView-stat-value">{presentDays}</span>
                                        <span className="attendanceView-stat-label">Present</span>
                                    </div>
                                    <div className="attendanceView-stat absent">
                                        <span className="attendanceView-stat-value">{totalDays - presentDays}</span>
                                        <span className="attendanceView-stat-label">Absent</span>
                                    </div>
                                </div>
                                <div className="attendanceView-percentage-bar">
                                    <div
                                        className="attendanceView-percentage-fill"
                                        style={{ width: `${percentage}%` }}
                                    ></div>
                                    <span className="attendanceView-percentage-text">{percentage}%</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <div className="attendanceView-container">
            <ToastContainer position="top-right" autoClose={3000} />

            <div className="attendanceView-header">
                <h2>View Attendance Records</h2>
                <div className="attendanceView-month-selector">
                    <button onClick={() => handleMonthChange(-1)} className="attendanceView-month-nav">
                        <ChevronLeft size={20} />
                    </button>
                    <span>{months[currentMonth]} {currentYear}</span>
                    <button onClick={() => handleMonthChange(1)} className="attendanceView-month-nav">
                        <ChevronRight size={20} />
                    </button>
                </div>
            </div>

            <div className="attendanceView-usertype">
                <button
                    className={`attendanceView-usertype-btn ${activeUserType === 'student' ? 'active' : ''}`}
                    onClick={() => setActiveUserType('student')}
                >
                    Students
                </button>
                <button
                    className={`attendanceView-usertype-btn ${activeUserType === 'teacher' ? 'active' : ''}`}
                    onClick={() => setActiveUserType('teacher')}
                >
                    Teachers
                </button>
            </div>

            <div className="attendanceView-search">
                <Search size={20} />
                <input
                    type="text"
                    placeholder={`Search ${activeUserType}s...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            {isLoading && users.length === 0 ? (
                <>
                    <Loader />
                    <div className="attendanceView-loading">Loading users...</div>
                </>

            ) : (
                <div className="attendanceView-users-list">
                    {users
                        .filter(user =>
                            `${user.firstName} ${user.lastName}`
                                .toLowerCase()
                                .includes(searchTerm.toLowerCase()) ||
                            (activeUserType === 'student' && user.rollNumber &&
                                user.rollNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
                            (activeUserType === 'teacher' && user.employeeId &&
                                user.employeeId.toLowerCase().includes(searchTerm.toLowerCase()))
                        )
                        .map(user => {
                            const { totalDays, presentDays, absentDays, attendancePercentage } = getMonthlyStats(user._id);
                            const isExpanded = expandedUser === user._id;

                            return (
                                <div key={user._id} className="attendanceView-user-card">
                                    <div className="attendanceView-user-header">
                                        <div className="attendanceView-user-info">
                                            <span className="attendanceView-name">
                                                {user.firstName} {user.lastName}
                                            </span>
                                            <span className="attendanceView-id">
                                                {activeUserType === 'student' ? user.rollNumber : user.employeeId}
                                            </span>
                                        </div>
                                        <div className="attendanceView-user-stats">
                                            <div className="attendanceView-stat-item">
                                                <span className="attendanceView-stat-label">Present:</span>
                                                <span className="attendanceView-stat-value present">{presentDays}</span>
                                            </div>
                                            <div className="attendanceView-stat-item">
                                                <span className="attendanceView-stat-label">Absent:</span>
                                                <span className="attendanceView-stat-value absent">{absentDays}</span>
                                            </div>
                                            <div className="attendanceView-stat-item">
                                                <span className="attendanceView-stat-label">Rate:</span>
                                                <span className="attendanceView-stat-value">{attendancePercentage}%</span>
                                            </div>
                                        </div>
                                        <div className="attendanceView-actions">
                                            <button
                                                className="attendanceView-view-btn"
                                                onClick={() => toggleUserDetails(user._id)}
                                                title="View Details"
                                            >
                                                <Eye size={18} />
                                                View {isExpanded ? 'Less' : 'More'}
                                            </button>
                                        </div>
                                    </div>

                                    <div className={`attendanceView-calendar ${isExpanded ? 'expanded' : ''}`}>
                                        <div className="attendanceView-calendar-header">
                                            <Calendar size={18} />
                                            <span>Attendance Calendar - {months[currentMonth]} {currentYear}</span>
                                        </div>
                                        <div className="attendanceView-calendar-grid">
                                            {renderCalendarDays(user._id)}
                                        </div>

                                        {isExpanded && renderYearlyAttendance(user._id)}
                                    </div>
                                </div>
                            );
                        })}

                    {users.length === 0 && !isLoading && (
                        <div className="attendanceView-no-users">
                            No {activeUserType}s found. Please add some {activeUserType}s first.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default AttendanceView;